import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { catchError, map, Observable, Subject, throwError, tap } from 'rxjs';
import { jwtDecode } from 'jwt-decode';

import { RegisterRequest } from '../../shared/models/register.request';
import { InvitePreview } from '../../shared/models/system';
import { environment } from '../../../environments/environment.dev';

@Injectable({
  providedIn: 'root',
})
export class Auth {
  username: string | null = null;
  private isAuthenticated = false;

  private readonly sessionExpiredSubject = new Subject<void>();
  readonly sessionExpired$ = this.sessionExpiredSubject.asObservable();

  private apiUrl = `${environment.apiUrl}/auth`;
  private userUrl = `${environment.apiUrl}/User`;

  constructor(private http: HttpClient) {}


  registerUser(user: RegisterRequest): Observable<any> {
    return this.http.post(`${this.apiUrl}/register`, user).pipe(
      catchError(this.handleError)
    );
  }

  getInvitePreview(token: string): Observable<InvitePreview> {
    return this.http.get<InvitePreview>(`${this.apiUrl}/invite/${encodeURIComponent(token)}`).pipe(
      catchError(this.handleError)
    );
  }

  private handleError(error: HttpErrorResponse) {
    console.error("Error en la API:", error);
    return throwError(() => error);
  }

  login(username: string, password: string): Observable<any> {
    console.log('Intentando login con:', { username, password });
    return this.http.post(`${this.apiUrl}/login`, { username, password }).pipe(
      map((response: any) => {
        console.log('Login exitoso. Respuesta del backend:', response);
        this.isAuthenticated = true;

        const token = response.token;
        const idUser = response.user;

        console.log('Token recibido:', token);
        console.log('ID de usuario:', idUser);

        if (token && idUser !== undefined) {
          this.saveToken(token, idUser);
          console.log('Token guardado correctamente');
        }
        console.warn('Login exitoso pero faltan datos (token o user)');
        return response;
      }),
      catchError((error) => {
        this.isAuthenticated = false;
        console.error('Error en login:', error);
        console.error('Status:', error.status);
        console.error('Mensaje:', error.error);
        return throwError(() => error);
      })
    );
  }

  logout(): Observable<any> {
    const token = this.getToken();

    if (!token) {
      this.clearSession();
      return new Observable(obs => { obs.next(true); obs.complete(); });
    }

    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });

    return this.http.post(`${this.apiUrl}/logout`, {}, { headers }).pipe(
      tap(() => this.clearSession()),
      catchError(err => {
        this.clearSession();
        return throwError(() => err);
      })
    );
  }

  private static readonly TOKEN_KEY = 'authToken';
  private static readonly USER_ID_KEY = 'idUser';

  private clearSession(): void {
    localStorage.removeItem(Auth.TOKEN_KEY);
    localStorage.removeItem(Auth.USER_ID_KEY);
    sessionStorage.removeItem(Auth.TOKEN_KEY);
    sessionStorage.removeItem(Auth.USER_ID_KEY);
    this.isAuthenticated = false;
  }

  saveToken(token: string, idUser: number): void {
    if (token && idUser !== undefined) {
      localStorage.setItem(Auth.TOKEN_KEY, token);
      localStorage.setItem(Auth.USER_ID_KEY, idUser.toString());
    }
  }

  /** Token en localStorage (compartido entre pestañas). Migra una vez desde sessionStorage si existía sesión previa. */
  getToken(): string | null {
    let token = localStorage.getItem(Auth.TOKEN_KEY);
    if (!token) {
      const legacy = sessionStorage.getItem(Auth.TOKEN_KEY);
      if (legacy) {
        localStorage.setItem(Auth.TOKEN_KEY, legacy);
        const id = sessionStorage.getItem(Auth.USER_ID_KEY);
        if (id) localStorage.setItem(Auth.USER_ID_KEY, id);
        sessionStorage.removeItem(Auth.TOKEN_KEY);
        sessionStorage.removeItem(Auth.USER_ID_KEY);
        token = legacy;
      }
    }
    if (token && this.isTokenExpired(token)) {
      this.expireSession();
      return null;
    }
    return token;
  }

  isTokenExpired(token: string | null): boolean {
    if (!token) return true;
    try {
      const { exp } = jwtDecode<{ exp?: number }>(token);
      if (!exp) return false;
      return exp * 1000 <= Date.now();
    } catch {
      return true;
    }
  }

  expireSession(): void {
    const hadSession = !!localStorage.getItem(Auth.TOKEN_KEY) || !!sessionStorage.getItem(Auth.TOKEN_KEY);
    this.clearSession();
    if (hadSession) {
      this.sessionExpiredSubject.next();
    }
  }

  isLoggedIn(): boolean {
    return !!this.getToken();
  }

  getUsernameFromToken(token: string): string {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.username || payload.sub || '';
    } catch (error) {
      return '';
    }
  }

  getUserInfoFromToken(): Observable<any> {
    const token = this.getToken();
    if (!token) return throwError(() => new Error('Token not found'));

    const headers = new HttpHeaders({ 'Authorization': `Bearer ${token}` });

    return this.http.get(`${this.userUrl}/me`, { headers });
  }

  updateUser(userData: any): Observable<any> {
    const token = this.getToken();
    const idUser = localStorage.getItem(Auth.USER_ID_KEY);
    if (!idUser || idUser === 'undefined') {
      console.error('Error Fatal: No se encontró ID de usuario en sesión.');
      return throwError(() => new Error('No user ID found'));
    }
    const headers = new HttpHeaders({ 'Authorization': `Bearer ${token}` });
    const cleanData: any = {
      fullname: userData.fullname,
      email: userData.email,
      telephone: userData.telephone,
      birthDate: userData.birthDate,
    };
    if (userData.distritoId !== undefined) cleanData.distritoId = userData.distritoId;
    if (userData.direccion !== undefined) cleanData.direccion = userData.direccion;
    console.log('Enviando actualización para ID:', idUser, cleanData);
    return this.http.put(`${this.userUrl}/updateUser/${idUser}`, cleanData, { headers });
  }

  // recuperar contraseña

  forgotPassword(email: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/forgot-password`, { email });
  }

  verifyRecoveryCode(email: string, code: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/verify-code`, { email, code });
  }

  resetPassword(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/change-password`, data);
  }

  private decodeToken(token: string): any {
    try {
      return JSON.parse(atob(token.split('.')[1]));
    } catch (e) {
      return null;
    }
  }

  isAdmin(): boolean {
    const token = this.getToken();
    if (!token) return false;

    const payload = this.decodeToken(token);
    return payload && payload.role === 'ROLE_ADMIN';
  }
}